const { dayjs } = require('./date');
const { LicenseInterval, OrderItemType, DiscountType } = require('../services/prisma/types');
const { MODULE_LIMIT_SETTING_LABEL } = require('../constants/settings');
const { round } = require('../constants/math');
const { visitLimitOptions } = require('../constants/visitLimits');
const { EUR } = require('../constants/currencies');
const { availableAddons } = require('../constants/license');
const { numberFormat } = require('./formatter');
const { getModuleLimitSetting } = require('./license');

function addonIncluded({ addon, license }) {
  if (license?.settings[addon]) {
    return true;
  }
  return false;
}

function getSubtotal(order) {
  return order.items.reduce((acc, item) => (item.amount != null ? (acc || 0) + item.amount * item.quantity : acc), null);
}

function getDiscount(order, total) {
  if (!order.discount) {
    return 0;
  }

  return order.discountType === DiscountType.Percentage ? round(total * (order.discount / 100), 2) : order.discount;
}

function getTotal(order) {
  let total = getSubtotal(order);

  const discount = getDiscount(order, total);
  total -= discount;

  return total;
}

function createLegacyLicenseOrder({ settings, interval, license, startDate, endDate }) {
  const visitLimit = parseInt(settings.find(s => s.key === 'matomoMonthlyVisitLimit')?.value || 0, 10);
  const prices = license?.settings?.[interval === LicenseInterval.Yearly ? 'yearlyPrices' : 'monthlyPrices']?.find(p => p.matomoMonthlyVisitLimit.toString() === visitLimit.toString());
  const quantity = interval === LicenseInterval.Yearly ? 12 : 1;

  const items = [];

  if (license) {
    const { title } = license;
    let description;
    const vistLimitLabel = visitLimitOptions.find(o => o.value === visitLimit)?.label;
    if (vistLimitLabel) {
      description = `${vistLimitLabel} visits/month`;
    }

    items.push({
      title,
      description,
      amount: prices?.analytics ? parseFloat(prices?.analytics) : undefined,
      type: OrderItemType.License,
      quantity,
      limit: visitLimit,
      relationId: license.id,
      startDate,
      endDate,
    });
  }

  for (const { value: addon, label } of availableAddons) {
    if (addonIncluded({ addon, license })) {
      items.push({ title: `${label} (included)`, amount: 0, quantity: 1, type: OrderItemType.Addon, relationId: addon });
    } else if (settings.find(s => s.key === addon)?.value) {
      items.push({ title: label, amount: parseFloat(prices?.[addon] || 0), type: OrderItemType.Addon, quantity: 1, relationId: addon });
    }
  }

  return items;
}

function createModuleLicenseOrder({ modules, settings, interval, currency, licenses }) {
  const items = [];
  const addons = {};

  for (const licenseId of modules) {
    const license = licenses.find(l => l.id === licenseId);

    if (license) {
      const setting = getModuleLimitSetting(license?.module, license?.level);
      const limit = parseInt(settings?.find(s => s.key === setting)?.value || 0, 10);

      const prices = license?.settings.prices.find(p => p[setting] === limit);

      let quantity;
      let amount;

      if (interval === LicenseInterval.Yearly && prices?.yearlyPrice) {
        quantity = 1;
        amount = parseFloat(prices?.yearlyPrice?.[currency] || 0);
      } else {
        quantity = interval === LicenseInterval.Yearly ? 12 : 1;
        amount = interval === LicenseInterval.Trial ? 0 : parseFloat(prices?.monthlyPrice?.[currency] || 0);
        if (amount && interval === LicenseInterval.Yearly) {
          amount = Math.round(amount * 0.8);
        }
      }

      items.push({
        title: license.title,
        description: `${numberFormat(limit)} ${MODULE_LIMIT_SETTING_LABEL[setting]}`,
        amount,
        quantity,
        limit,
        type: OrderItemType.License,
        relationId: license.id,
      });

      for (const addonId of settings?.find(s => s.key === `${license.module}-addons`)?.value || []) {
        const addon = licenses.find(l => l.id === addonId);

        if (addon) {
          if (!addons[addon.id]) {
            const addonAmount = addon.settings.monthlySinglePrice[currency];
            addons[addon.id] = ({ title: addon.title, amount: addonAmount, description: license.title, type: OrderItemType.Addon, relationId: addon.id });
            addons[addon.id].quantity = 1;
          } else {
            addons[addon.id].amount = addon.settings.monthlyMultiplePrice[currency];
            addons[addon.id].description += `, ${license.title}`;
          }
        }
      }
    }
  }

  items.push(...Object.values(addons));

  return items;
}

function createOrder({
  modules,
  settings,
  interval,
  startDate,
  endDate,
  currency = EUR,
  license,
  licenses,
  dataSource,
  discount,
  discountType = DiscountType.Percentage,
}) {
  if (!endDate) {
    endDate = dayjs(startDate).add(1, interval === LicenseInterval.Yearly ? 'year' : 'month').subtract(1, 'day').endOf('day')
      .toDate();
  }

  const newOrder = {
    dataSource: dataSource.title,
    items: [],
    discount,
    discountType,
    currency,
  };

  let intervalTitle = 'Monthly';
  if (interval === LicenseInterval.Yearly) {
    intervalTitle = 'Yearly';
  }
  if (interval === LicenseInterval.Trial) {
    intervalTitle = 'Free trial';
  }

  newOrder.items.push({
    title: intervalTitle,
    description: `${dayjs(startDate).format('MMM DD, YYYY')} - ${dayjs(endDate).format('MMM DD, YYYY')}`,
  });

  if (license) {
    newOrder.items.push(...createLegacyLicenseOrder({ settings, interval, license, startDate, endDate }));
  }

  if (modules && licenses) {
    newOrder.items.push(...createModuleLicenseOrder({ modules, settings, interval, currency, licenses }));
  }

  newOrder.amount = getTotal(newOrder);

  return newOrder;
}

module.exports = {
  createOrder,
  getSubtotal,
  getDiscount,
  getTotal,
};
